import {
  borderRadiusXL,
  colorAccent1,
  colorBlue,
  colorGray,
  colorGreen,
  colorInfo,
  colorPurple,
  colorRepBg,
  colorYellow,
  fontSize,
  fontWeightStrong,
} from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import {
  ActivePeriod,
  DataObjectOutputType,
  DataObjectRelationEdgeType,
  DataObjectRelationNodeType,
  DataObjectRelationVersionType,
  DataObjectType,
  DataObjectVersionType,
  getLatestDataObjectVersion,
  getValidationErrorLevelColor,
  ISODate,
  StyledTooltipContent,
  SystemClassifierItemDto,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import {
  Edge,
  Handle,
  Node,
  NodeProps,
  NodeToolbar,
  Position,
} from '@xyflow/react';
import { Flex } from 'antd';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { SpinningLoaderIcon } from './SpinningLoaderIcon';
import { VersionSpan } from '../output-data/VersionCell';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RelationNode.translations.json';
import {
  RelationsLegendItemIcon,
  RelationsLegendItemIconText,
} from './RelationsLegend';
import databaseRedIcon from '../../assets/img/database-red.svg';
import databaseGreenIcon from '../../assets/img/database-green.svg';
import dataConflictsIcon from '../../assets/img/data-conflicts.svg';
import unknownDataConflictsIcon from '../../assets/img/unknown-data-conflicts.svg';

const { addTranslations, useTranslation } = i18nNsFactory('relation-node');
addTranslations(translations);

export type RelationNodeType = Node<NodeData>;
export type RelationEdgeType = Edge<{
  relationType: DataObjectRelationEdgeType;
}>;
export type NodeData = {
  label: string;
  isRootNode: boolean;
  isInRootGroup: boolean;
  dataObjectType: DataObjectType;
  outputType?: DataObjectOutputType | null;
  domainName?: string | null;
  dataObjectVersions?: DataObjectRelationVersionType[] | null;
  dataObjectGroupCode?: string | null;
  nodeType?: string;
  childNodes?: NodeData[];
  missingSourceData?: boolean;
  validationErrorLevelId?: number | null;
  calculationErrors?: boolean;
  hasActiveProcessing?: boolean;
  descriptionId: number | null;
  groupId: number | null;
  activePeriods: ActivePeriod[] | null;
  frequency: SystemClassifierItemDto | null;
  activeVersion: DataObjectVersionType | null;
  latestValueDate: ISODate | null;
  loadingExists: boolean;
  hasAccess: boolean;
};
const validationStatusRanking: Record<string, number> = {
  ERROR: 1,
  TECH_ERROR: 1,
  WARNING: 2,
  INFO: 3,
  SUCCESS: 4,
  NO_RULES: 5,
};

export const RelationNode = ({ data }: NodeProps<RelationNodeType>) => {
  const { t } = useTranslation();
  const isFocusedNode = data.isRootNode;
  const borderWidth = isFocusedNode ? 3 : 1;

  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'PROCESSING_ERROR_LEVEL',
    'PROC_VAL_ERROR_LEVEL',
  ]);

  const { PROCESSING_ERROR_LEVEL, PROC_VAL_ERROR_LEVEL } =
    systemClassifierItemsById;

  const latestVersion = useMemo(() => {
    const versions = data.dataObjectVersions;

    if (!versions || versions.length === 0) {
      return null;
    }
    return getLatestDataObjectVersion(versions);
  }, [data]);

  const [searchParams] = useSearchParams();
  const versionFilterValue = searchParams.get('version');

  const isGroupNode = data.nodeType === DataObjectRelationNodeType.GROUP;
  const dataObjectType = data.dataObjectType;

  const withChildNodes = useMemo(
    () => [data, ...(data.childNodes ?? [])],
    [data],
  );

  const { containsIntermediates, containsOnlyIntermediates } = useMemo(() => {
    const children = data?.childNodes ?? [];
    const containsIntermediates = children.some(
      (c) => c?.outputType === DataObjectOutputType.INTERMEDIATE,
    );

    const containsOnlyIntermediates =
      children.length > 0 &&
      children.every(
        (c) => c?.outputType === DataObjectOutputType.INTERMEDIATE,
      );

    return { containsIntermediates, containsOnlyIntermediates };
  }, [data?.childNodes]);

  const { hasErrorIcon, hasInfoIcon, hasActiveProcessing, hasLoading } =
    useMemo(() => {
      const hasActivePrc = withChildNodes.some(
        (node) => node?.hasActiveProcessing === true,
      );
      const hasLoading = withChildNodes.some((node) => !!node?.loadingExists);
      if (dataObjectType === DataObjectType.IN) {
        const hasErrorCode = withChildNodes.some((node) =>
          node?.validationErrorLevelId
            ? PROCESSING_ERROR_LEVEL.get(node?.validationErrorLevelId)?.code ===
              'ERROR'
            : false,
        );
        const hasInfoCode = withChildNodes.some((node) =>
          node?.validationErrorLevelId
            ? PROCESSING_ERROR_LEVEL.get(node?.validationErrorLevelId)?.code ===
              'INFO'
            : false,
        );

        return {
          hasErrorIcon: hasErrorCode,
          hasInfoIcon: hasInfoCode && !hasErrorCode,
          hasActiveProcessing: hasActivePrc,
          hasLoading,
        };
      }

      const hasCalcErrors = withChildNodes.some(
        (node) => node?.calculationErrors === true,
      );
      return {
        hasErrorIcon: hasCalcErrors,
        hasInfoIcon: false,
        hasActiveProcessing: hasActivePrc,
      };
    }, [withChildNodes, dataObjectType, PROCESSING_ERROR_LEVEL]);

  const {
    hasNewerVersionAvailable,
    hasEmptyVersion,
    validationStatus,
    hasKnownConflict,
    hasUnknownConflict,
  } = useMemo(() => {
    const result = withChildNodes.reduce(
      (acc, node) => {
        const rawVersions = node?.dataObjectVersions ?? [];
        const latestOnly =
          data.isInRootGroup && versionFilterValue === 'latest'
            ? (getLatestDataObjectVersion(
                rawVersions,
              ) as DataObjectRelationVersionType | null)
            : null;
        const versions = latestOnly ? [latestOnly] : rawVersions;

        const validationLevelIds: number[] = [];
        if (
          dataObjectType === DataObjectType.IN &&
          node?.validationErrorLevelId != null
        ) {
          validationLevelIds.push(node.validationErrorLevelId);
        }

        if (versions.length === 0) return acc;

        if (dataObjectType !== DataObjectType.IN) {
          for (const v of versions) {
            if (v.validationErrorLevelId != null) {
              validationLevelIds.push(v.validationErrorLevelId);
            }
          }
        }

        for (const levelId of validationLevelIds) {
          const levelItem = PROC_VAL_ERROR_LEVEL.get(levelId);
          const code = levelItem?.code;
          if (code) {
            const ranking =
              validationStatusRanking[code] ??
              validationStatusRanking['NO_RULES'];
            const currentRanking = acc.validationStatus
              ? (validationStatusRanking[acc.validationStatus] ??
                validationStatusRanking['NO_RULES'])
              : 6;
            if (ranking < currentRanking) {
              acc.validationStatus = code;
            }
          }
        }

        if (!acc.hasEmptyVersion && versions.some((v) => v.emptyVersion)) {
          acc.hasEmptyVersion = true;
        }

        if (!acc.hasNewerVersionAvailable) {
          const latest = getLatestDataObjectVersion(versions);
          if (latest?.newerVersionAvailable === true) {
            acc.hasNewerVersionAvailable = true;
          }
        }

        const dataConflict = (
          node.activeVersion as DataObjectRelationVersionType | null
        )?.dataConflict;

        if (dataConflict === true) acc.anyConflictTrue = true;
        if (dataConflict === null) acc.anyConflictNull = true;

        return acc;
      },
      {
        hasNewerVersionAvailable: false,
        hasEmptyVersion: false,
        validationStatus: null as string | null,
        anyConflictTrue: false,
        anyConflictNull: false,
      },
    );

    const isOutput = dataObjectType !== DataObjectType.IN;
    const hasKnownConflict = isOutput && result.anyConflictTrue;
    const hasUnknownConflict =
      isOutput && !result.anyConflictTrue && result.anyConflictNull;

    return { ...result, hasKnownConflict, hasUnknownConflict };
  }, [
    withChildNodes,
    PROC_VAL_ERROR_LEVEL,
    dataObjectType,
    data.isInRootGroup,
    versionFilterValue,
  ]);

  return (
    <StyledNode
      borderWidth={borderWidth}
      className={
        dataObjectType === DataObjectType.IN || !data.outputType
          ? DataObjectType.IN
          : data.outputType
      }
    >
      <NodeIconsContainer>
        <NodeIconsGroup>
          {isGroupNode && !containsOnlyIntermediates && (
            <RelationsLegendItemIcon>G</RelationsLegendItemIcon>
          )}
          {containsIntermediates && (
            <RelationsLegendItemIcon>
              <RelationsLegendItemIconText color={colorPurple[5]}>
                VV
              </RelationsLegendItemIconText>
            </RelationsLegendItemIcon>
          )}
        </NodeIconsGroup>
        <NodeIconsGroup>
          {hasInfoIcon && (
            <RelationsLegendItemIcon>
              <Icon.AlertTriangle size={18} color={colorInfo} />
            </RelationsLegendItemIcon>
          )}
          {hasErrorIcon && (
            <RelationsLegendItemIcon>
              <Icon.AlertTriangle size={18} color={colorAccent1} />
            </RelationsLegendItemIcon>
          )}
          {validationStatus && (
            <RelationsLegendItemIcon>
              <RelationsLegendItemIconText
                color={getValidationErrorLevelColor(validationStatus)}
              >
                VR
              </RelationsLegendItemIconText>
            </RelationsLegendItemIcon>
          )}
          {hasKnownConflict && (
            <RelationsLegendItemIcon hasPadding>
              <img src={dataConflictsIcon} alt="data-conflicts-icon" />
            </RelationsLegendItemIcon>
          )}
          {hasUnknownConflict && (
            <RelationsLegendItemIcon hasPadding>
              <img
                src={unknownDataConflictsIcon}
                alt="unknown-data-conflicts-icon"
              />
            </RelationsLegendItemIcon>
          )}
          {hasActiveProcessing && (
            <RelationsLegendItemIcon>
              <SpinningLoaderIcon size={18} color={colorGray[7]} />
            </RelationsLegendItemIcon>
          )}
          {hasEmptyVersion && (
            <RelationsLegendItemIcon>
              <img src={databaseRedIcon} alt="database-red-icon" />
            </RelationsLegendItemIcon>
          )}
          {hasLoading && (
            <RelationsLegendItemIcon>
              <img src={databaseGreenIcon} alt="database-green-icon" />
            </RelationsLegendItemIcon>
          )}
          {hasNewerVersionAvailable && (
            <RelationsLegendItemIcon>
              <Icon.Database size={18} color={colorYellow[5]} />
            </RelationsLegendItemIcon>
          )}
        </NodeIconsGroup>
      </NodeIconsContainer>
      <StyledNodeHandle type="target" position={Position.Left} />
      <div>
        <NodeLabel isFocused={isFocusedNode}>{data.label}</NodeLabel>
        {data.activeVersion && (
          <NodeVersionLabel justify="center" gap={6}>
            {data.activeVersion.versionNumber != null && (
              <VersionSpan>{`${data.activeVersion.versionNumber}${
                latestVersion?.minorNumber
                  ? `.${latestVersion.minorNumber}`
                  : ''
              }`}</VersionSpan>
            )}
            {data.activeVersion.statusCode && (
              <NodeVersionStatusLabel>
                {t(`status.${data.activeVersion.statusCode}`)}
              </NodeVersionStatusLabel>
            )}
          </NodeVersionLabel>
        )}
      </div>

      <StyledNodeHandle type="source" position={Position.Right} id="a" />

      <NodeToolbar position={Position.Bottom}>
        <StyledTooltipContent $variant="dark">
          <div>{data.domainName}</div>
          <div>{data.label}</div>
        </StyledTooltipContent>
      </NodeToolbar>
    </StyledNode>
  );
};

type StyledNodeProps = {
  borderWidth: number;
};
const StyledNode = styled.div<StyledNodeProps>`
  border: ${({ borderWidth }) => `${borderWidth}px`} solid;
  height: 100%;
  width: 100%;
  padding: 14px;
  border-radius: ${borderRadiusXL}px;
  text-align: center;
  font-size: 16px;

  &:is(.IN) {
    border-color: ${colorGreen[6]};
    background-color: ${colorGreen[2]};
  }
  &:is(.OUTPUT) {
    border-color: ${colorYellow[6]};
    background-color: ${colorYellow[2]};
  }
  &:is(.INTERMEDIATE) {
    border-color: ${colorPurple[6]};
    background-color: ${colorPurple[2]};
  }
  &:is(.REPORT) {
    border-color: ${colorBlue[6]};
    background-color: ${colorRepBg};
  }
`;

const StyledNodeHandle = styled(Handle)`
  visibility: hidden;
  width: 0;
  height: 0;
  transform: -0.5px -0.5px;
`;

type NodeLabelProps = {
  isFocused: boolean;
};
const NodeLabel = styled(Flex)<NodeLabelProps>`
  font-weight: ${({ isFocused }) => (isFocused ? 700 : 400)};
  text-align: left;
  height: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
`;

const NodeVersionLabel = styled(Flex)`
  margin-top: 0.5rem;
`;

const NodeVersionStatusLabel = styled.span`
  font-size: 11px;
  font-weight: ${fontWeightStrong};
  letter-spacing: 0.1em;
`;

const NodeIconsContainer = styled.div`
  position: absolute;
  top: -0.6rem;
  left: 0.3rem;
  right: 0.3rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  font-size: ${fontSize}px;
`;

const NodeIconsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
`;
